import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from '../lib/toast';

/**
 * Estados do autosave:
 * - 'idle': Conteúdo está salvo e não há alterações pendentes (botão desabilitado)
 * - 'editing': Usuário está editando, mudanças detectadas mas ainda não disparou autosave
 * - 'saving': Autosave está sendo executado no momento
 * - 'saved': Salvamento acabou de ser concluído com sucesso (transição temporária para mostrar feedback)
 * - 'error': Erro ao tentar salvar
 */
export type SaveStatus = 'idle' | 'editing' | 'saving' | 'saved' | 'error';

interface UseAutoSaveOptions<T> {
  data: T;
  onSave: (data: T) => Promise<void>;
  delay?: number;
  maxWait?: number;
  enabled?: boolean;
}

export function useAutoSave<T>({
  data,
  onSave,
  delay = 5000,
  maxWait = 120000,
  enabled = true,
}: UseAutoSaveOptions<T>) {
  // Estado principal: controla o status visual do botão e indicador
  const [status, setStatus] = useState<SaveStatus>('idle');
  // Flag de controle: informa se há mudanças não salvas
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const maxWaitTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastSavedDataRef = useRef<T>(data);
  const isSavingRef = useRef(false);
  const isInitializedRef = useRef(false);
  const savedFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * Função principal de salvamento
   * Gerencia os estados: saving → saved → idle
   */
  const save = useCallback(async () => {
    if (isSavingRef.current || !enabled) return;

    // Verifica se há mudanças reais comparando JSON
    const hasChanges = JSON.stringify(data) !== JSON.stringify(lastSavedDataRef.current);
    if (!hasChanges) {
      setStatus('idle');
      setHasUnsavedChanges(false);
      return;
    }

    try {
      isSavingRef.current = true;
      setStatus('saving'); // Estado 1: Salvando
      setHasUnsavedChanges(true); // Mantém true enquanto salva

      await onSave(data);

      // Sucesso: atualiza referência e marca como salvo
      lastSavedDataRef.current = data;
      setStatus('saved'); // Estado 2: Salvo (feedback temporário)
      setHasUnsavedChanges(false);

      // Limpa backup local após sucesso
      if (typeof window !== 'undefined') {
        localStorage.removeItem('autoSaveBackup');
      }

      // Após 2 segundos, transiciona para 'idle' (botão desativado)
      if (savedFeedbackTimeoutRef.current) {
        clearTimeout(savedFeedbackTimeoutRef.current);
      }
      savedFeedbackTimeoutRef.current = setTimeout(() => {
        setStatus('idle'); // Estado 3: Idle (sem alterações pendentes)
      }, 2000);

    } catch (error) {
      console.error('Auto-save error:', error);
      setStatus('error');
      setHasUnsavedChanges(true);

      // Mensagem de erro detalhada para o usuário
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      const isNetworkError = errorMessage.includes('fetch') || errorMessage.includes('conexão') || errorMessage.includes('internet') || errorMessage.includes('network');
      const isTimeoutError = errorMessage.includes('timeout') || errorMessage.includes('tempo limite') || errorMessage.includes('demorou');
      const isAuthError = errorMessage.includes('sessão') || errorMessage.includes('login') || errorMessage.includes('401');

      if (isAuthError) {
        toast.error('Salvamento automático falhou — sua sessão expirou. Não recarregue a página e faça login em outra aba para não perder as alterações.');
      } else if (isNetworkError) {
        toast.error('Salvamento automático falhou — sem conexão com a internet. Não recarregue a página. Salvaremos automaticamente quando a conexão voltar.');
      } else if (isTimeoutError) {
        toast.error('Salvamento automático falhou — o servidor demorou para responder. Não recarregue a página. Tentaremos novamente em breve.');
      } else {
        toast.error(`Salvamento automático falhou: ${errorMessage}. Não recarregue a página e tente salvar manualmente.`);
      }

      // Backup local em caso de erro
      if (typeof window !== 'undefined') {
        localStorage.setItem('autoSaveBackup', JSON.stringify(data));
      }
    } finally {
      isSavingRef.current = false;
    }
  }, [data, onSave, enabled]);


  /**
   * Effect principal: detecta mudanças e agenda autosave
   * Gerencia transição de estados: idle → editing → (trigger autosave)
   */
  useEffect(() => {
    if (!enabled) return;

    // Inicialização: marca como idle (sem mudanças)
    // IMPORTANTE: Aguarda os dados estarem disponíveis antes de inicializar
    if (!isInitializedRef.current) {
      // Se os dados ainda são null, aguarda a próxima renderização
      if (data === null || data === undefined) {
        return;
      }

      // FIX: Salva uma cópia profunda dos dados iniciais para evitar falsas mudanças
      lastSavedDataRef.current = JSON.parse(JSON.stringify(data));
      isInitializedRef.current = true;
      setStatus('idle');
      setHasUnsavedChanges(false);
      return;
    }

    // Cancela timeout anterior se houver
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Compara dados atuais com última versão salva
    const hasChanges = JSON.stringify(data) !== JSON.stringify(lastSavedDataRef.current);

    // Sem mudanças: garante estado idle
    if (!hasChanges) {
      // Cancela feedback timeout se usuário desfez mudanças antes do idle
      if (savedFeedbackTimeoutRef.current) {
        clearTimeout(savedFeedbackTimeoutRef.current);
        savedFeedbackTimeoutRef.current = undefined;
      }
      setStatus('idle');
      setHasUnsavedChanges(false);
      return;
    }

    // Mudanças detectadas e não está salvando: transiciona para 'editing'
    if (hasChanges && !isSavingRef.current) {
      // Cancela feedback timeout se nova edição começou durante feedback
      if (savedFeedbackTimeoutRef.current) {
        clearTimeout(savedFeedbackTimeoutRef.current);
        savedFeedbackTimeoutRef.current = undefined;
      }

      setStatus('editing');
      setHasUnsavedChanges(true);

      // Timeout de segurança: salva após maxWait mesmo se continuar editando
      if (!maxWaitTimeoutRef.current) {
        maxWaitTimeoutRef.current = setTimeout(() => {
          save();
          maxWaitTimeoutRef.current = undefined;
        }, maxWait);
      }

      // Timeout principal: salva após delay de inatividade
      timeoutRef.current = setTimeout(() => {
        save();
        if (maxWaitTimeoutRef.current) {
          clearTimeout(maxWaitTimeoutRef.current);
          maxWaitTimeoutRef.current = undefined;
        }
      }, delay);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, delay, maxWait, save, enabled]);


  /**
   * Cleanup: limpa todos os timers ao desmontar
   */
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (maxWaitTimeoutRef.current) {
        clearTimeout(maxWaitTimeoutRef.current);
      }
      if (savedFeedbackTimeoutRef.current) {
        clearTimeout(savedFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const forceSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (maxWaitTimeoutRef.current) {
      clearTimeout(maxWaitTimeoutRef.current);
      maxWaitTimeoutRef.current = undefined;
    }
    return save();
  }, [save]);

  return {
    status,
    hasUnsavedChanges,
    forceSave,
  };
}
